import { App } from '@capacitor/app';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { appLinkTarget, isNativeApp } from './native';
import { KeepAwake } from './pos/wakeLock';

const LAUNCH_KEY = 'barso.native.launchHandled';

/**
 * `getLaunchUrl` liefert die Start-Adresse der App für die ganze Lebensdauer der Hülle: Nach einem Neuladen der
 * WebView würde sie sonst erneut abgearbeitet (und zum alten Kopplungs-Link zurückspringen). Die bearbeitete
 * Adresse steht deshalb in `sessionStorage` (gilt je App-Prozess). Ohne Speicher gilt „noch nicht bearbeitet“.
 */
function launchHandled(link: string): boolean {
  try {
    if (sessionStorage.getItem(LAUNCH_KEY) === link) return true;
    sessionStorage.setItem(LAUNCH_KEY, link);
  } catch {
    // ohne Speicher: einmal je Seitenaufruf
  }
  return false;
}

/**
 * Deep Links der nativen App: Öffnet ein Gerät eine Kopplungsadresse (`https://…/kasse-koppeln#token=…`) oder die
 * Verknüpfung „Kasse“, liefert die Hülle die Adresse samt Fragment, und die Web-App navigiert dorthin. Im Browser
 * ohne Wirkung. Dieselbe Adresse wird innerhalb weniger Sekunden nur einmal bearbeitet (Kaltstart meldet sie doppelt).
 * Beim Start wird außerdem ein hängen gebliebenes „Bildschirm wach“ der Hülle gelöst (die Kasse setzt es neu).
 */
export function useNativeLinks(): void {
  const navigate = useNavigate();
  useEffect(() => {
    if (isNativeApp()) void KeepAwake.disable().catch(() => undefined);
  }, []);
  const last = useRef<{ link: string; at: number } | null>(null);
  // Die Funktion wechselt mit jeder Adresse: Der Hörer bleibt trotzdem einmal je App-Start eingehängt
  const go = useRef(navigate);
  useEffect(() => {
    go.current = navigate;
  }, [navigate]);
  useEffect(() => {
    if (!isNativeApp()) return;
    const handle = (link: string): void => {
      const now = Date.now();
      if (last.current?.link === link && now - last.current.at < 3000) return;
      last.current = { link, at: now };
      const target = appLinkTarget(link);
      if (target !== null) void go.current(target);
    };
    const listener = App.addListener('appUrlOpen', (event) => {
      handle(event.url);
    });
    void App.getLaunchUrl()
      .then((launch) => {
        if (launch?.url !== undefined && !launchHandled(launch.url)) handle(launch.url);
      })
      .catch(() => undefined);
    return () => {
      void listener.then((registered) => registered.remove()).catch(() => undefined);
    };
  }, []);
}
